import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AdminRequest, listAdminRequests } from '../../api/marketplace';
import {
  URGENCY_LABELS,
  formatCountdown,
  formatCountries,
  serviceLabel,
  toDate,
  useNow,
} from './marketplaceFormat';
import './Marketplace.css';

const deadlineOf = (item: AdminRequest): number =>
  toDate(item.bidDeadline)?.getTime() ?? Number.MAX_SAFE_INTEGER;

// Coepi staff view of the live board: every request partners can bid on
// right now, across all services, with the bid count partners never see.
const AdminBoardPage: React.FC = () => {
  const { currentUser } = useAuth();
  const now = useNow();
  const [requests, setRequests] = useState<AdminRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const open = await listAdminRequests(token, 'OPEN');
      setRequests([...open].sort((a, b) => deadlineOf(a) - deadlineOf(b)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the board.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="marketplace-page">
      <div className="task-view-header">
        <h2>Marketplace board</h2>
        <div>
          <Link to="/marketplace/admin/requests" className="service-link">Triage</Link>{' '}
          <button type="button" className="download-button" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>
      <p className="marketplace-hint">
        Requests partner firms can bid on right now. Firms see only their own services and never
        the bid count or each other's bids.
      </p>
      {loading && requests.length === 0 ? (
        <p>Loading requests...</p>
      ) : error ? (
        <div className="status-error-banner" role="alert">{error}</div>
      ) : requests.length === 0 ? (
        <p className="marketplace-empty">No open requests right now.</p>
      ) : (
        <ul className="marketplace-cards">
          {requests.map(item => (
            <li key={item.id} className="marketplace-card">
              <Link to={`/marketplace/admin/requests/${item.id}`} className="marketplace-card-link">
                <div className="marketplace-card-head">
                  <strong>{serviceLabel(item.serviceType)}</strong>
                  <span className="marketplace-countdown">{formatCountdown(item.bidDeadline, now)}</span>
                </div>
                <div className="marketplace-meta">
                  <span>{formatCountries(item.countries)}</span>
                  <span>Language: {(item.language || 'n/a').toUpperCase()}</span>
                  <span className={`marketplace-urgency marketplace-urgency--${item.urgency}`}>
                    {URGENCY_LABELS[item.urgency] || item.urgency} urgency
                  </span>
                  <span className="marketplace-badge">
                    {item.bidCount ?? 0} {(item.bidCount ?? 0) === 1 ? 'bid' : 'bids'}
                  </span>
                </div>
                <p className="marketplace-summary">{item.anonymisedSummary}</p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminBoardPage;
